import { css, tokens, media } from '@sail/ui';
import { helmTokens } from '../../theme';
import { stripeStretch } from '../../lib/utils';
export const styles = {
    sectionRoot: css({
        position: 'relative',
        zIndex: 1,
        marginBottom: 'var(--sectionMarginBottom, 0)'
    }),
    sectionRootUnprocessed: css({
        [media.down('375px')]: {
            '--sectionAnglePaddingTopBase': 'calc(var(--sectionAnglePaddingBaseMin) * 1px)',
            '--sectionAnglePaddingBottomBase': 'calc(var(--sectionAnglePaddingBaseMin) * 1px)'
        },
        [media.between('375px', tokens.viewport.xlarge)]: {
            '--sectionAnglePaddingTopBase': `${stripeStretch('var(--sectionAnglePaddingBaseMin)', 'var(--sectionAnglePaddingBottomBaseMax)')}`,
            '--sectionAnglePaddingBottomBase': `${stripeStretch('var(--sectionAnglePaddingBaseMin)', 'var(--sectionAnglePaddingTopBaseMax)')}`
        },
        [media.up(tokens.viewport.xlarge)]: {
            '--sectionAnglePaddingTopBase': 'calc(var(--sectionAnglePaddingTopBaseMax) * 1px)',
            '--sectionAnglePaddingBottomBase': 'calc(var(--sectionAnglePaddingBottomBaseMax) * 1px)'
        },
        '--sectionAngleSin': 'var(--angleNormalSin)',
        '--sectionAngle': 0,
        '--sectionPaddingMin': 72,
        '--sectionAngleHeight': 'calc(var(--windowWidth) * var(--sectionAngleSin))',
        '--sectionAnglePaddingBaseMin': '100',
        '--sectionAnglePaddingBaseMax': 'var(--sectionPaddingMax, 128)',
        '--sectionAnglePaddingTopBaseMax': 'var(--sectionAnglePaddingBaseMax)',
        '--sectionAnglePaddingBottomBaseMax': 'var(--sectionAnglePaddingBaseMax)',
        '--sectionAngleMaxHeight': 'none',
        '--sectionTransformOrigin': '100% 0',
        '--sectionPaddingTopGutterWidth': 'var(--gutterWidth)',
        '--sectionAnglePaddingTop': 'calc(var(--sectionAngleHeight) - var(--sectionAngleSin) * var(--sectionPaddingTopGutterWidth) + var(--sectionAnglePaddingTopBase))',
        '--sectionAnglePaddingBottom': 'calc(var(--sectionAngleHeight) - var(--sectionAngleSin) * var(--sectionPaddingTopGutterWidth) + var(--sectionAnglePaddingBottomBase))'
    }),
    sectionMasked: css({
        overflow: 'hidden',
        position: 'relative'
    }),
    sectionBackgroundMask: css({
        position: 'absolute',
        width: '100%',
        height: '100%',
        overflow: 'visible'
    }),
    sectionBackground: css({
        position: 'relative',
        height: '100%',
        maxHeight: 'none',
        width: '100%',
        top: 0,
        left: 0,
        transformOrigin: 'var(--sectionTransformOrigin, 100% 0)',
        transform: 'skewY(var(--sectionAngle, 0))',
        backgroundColor: 'surface',
        overflow: 'hidden'
    }),
    sectionBackgroundHasBorderTop: css({
        borderTop: `1px solid ${helmTokens.color.gridLines.solid}`
    }),
    sectionContainer: [
        css({
            position: 'relative',
            zIndex: '1',
            stack: 'y',
            justifyContent: 'center'
        })
    ],
    sectionLayoutContainer: [
        css({
            width: '100%',
            maxWidth: `calc(100vw - var(--scrollbarWidth) - ${tokens.space.medium} * 2)`,
            marginX: 'medium',
            flexBasis: 'auto',
            flexGrow: 0,
            alignSelf: 'center',
            [media.up(tokens.viewport.xlarge)]: {
                maxWidth: '1080px'
            }
        })
    ],
    sectionLayout: [
        css({
            paddingY: `${stripeStretch(72, 128)}`,
            [media.down(tokens.viewport.xsmall)]: {
                paddingY: '72px'
            },
            [media.up(tokens.viewport.xlarge)]: {
                paddingY: '128px'
            }
        })
    ],
    variants: {
        padding: {
            xlarge: {
                subviews: {
                    sectionLayout: css({
                        paddingY: `${stripeStretch(72, 128)}`,
                        [media.down(tokens.viewport.xsmall)]: {
                            paddingY: '72px'
                        },
                        [media.up(tokens.viewport.large)]: {
                            paddingY: '128px'
                        }
                    })
                }
            },
            large: {
                subviews: {
                    sectionLayout: css({
                        paddingY: `${stripeStretch(72, 112)}`,
                        [media.down(tokens.viewport.xsmall)]: {
                            paddingY: '72px'
                        },
                        [media.up(tokens.viewport.large)]: {
                            paddingY: '112px'
                        }
                    })
                }
            },
            medium: {
                subviews: {
                    sectionLayout: css({
                        paddingY: `${stripeStretch(72, 80)}`,
                        [media.down(tokens.viewport.xsmall)]: {
                            paddingY: '72px'
                        },
                        [media.up(tokens.viewport.large)]: {
                            paddingY: '80px'
                        }
                    })
                }
            },
            small: {
                subviews: {
                    sectionLayout: css({
                        paddingY: '60px'
                    })
                }
            }
        },
        paddingTop: {
            none: {
                subviews: {
                    sectionLayout: css({
                        paddingTop: 0
                    })
                }
            }
        },
        paddingBottom: {
            none: {
                subviews: {
                    sectionLayout: css({
                        paddingBottom: 0
                    })
                }
            }
        },
        angle: {
            top: {
                uses: [
                    css({
                        '--sectionAngle': 'var(--angleNormal, -6deg)'
                    })
                ],
                subviews: {
                    sectionLayout: css({
                        paddingTop: 'var(--sectionAnglePaddingTop)'
                    })
                }
            },
            bottom: {
                uses: [
                    css({
                        '--sectionAngle': 'var(--angleNormal, -6deg)',
                        '--sectionMarginBottom': 'calc(var(--sectionAngleHeight, 0) * -1)',
                        '--sectionTransformOrigin': '0 0'
                    })
                ],
                subviews: {
                    sectionLayout: css({
                        paddingBottom: 'var(--sectionAnglePaddingBottom)'
                    })
                }
            },
            both: {
                uses: [
                    css({
                        '--sectionAngle': 'var(--angleNormal, -6deg)',
                        '--sectionMarginBottom': 'calc(var(--sectionAngleHeight, 0) * -1)'
                    })
                ],
                subviews: {
                    sectionLayout: css({
                        paddingTop: 'var(--sectionAnglePaddingTop)',
                        paddingBottom: 'var(--sectionAnglePaddingBottom)'
                    })
                }
            }
        },
        angleValue: {
            strong: {
                uses: [
                    css({
                        '--sectionAngle': 'var(--angleStrong)',
                        '--sectionAngleSin': 'var(--angleStrongSin)'
                    })
                ]
            }
        }
    }
};

//# sourceMappingURL=SectionStyles.js.map